<template>
  <div class="page-container">
    <!-- 侧边栏 -->
    <div 
      class="sidebar-container"
      :class="{ 'sidebar-collapsed': isSidebarCollapsed }"
    >
      <sidebar @sidebar-toggle="handleSidebarToggle" @new-chat="handleNewChat" />
    </div>
    
    <!-- 主要内容区域 -->
    <div 
      class="main-content"
      :style="contentStyle"
    >
      <div class="content-wrapper">
        <!-- 页面标题 -->
        <h1 class="page-title">
          病害识别数据分析
          <div class="title-underline"></div>
        </h1>
        
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">数据加载中...</p>
        </div>

        <div v-else class="main-sections">
          <!-- 统计概览 -->
          <div class="stats-grid">
            <!-- 总识别次数卡片 -->
            <div class="stat-card" data-aos="fade-up" data-aos-delay="100">
              <!-- 顶部装饰条 -->
              <div class="card-decoration"></div>
              
              <!-- 图标 -->
              <div class="card-icon-container">
                <div class="card-icon">
                  <svg class="icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 11H7C6.45 11 6 10.55 6 10V6C6 5.45 6.45 5 7 5H11C11.55 5 12 5.45 12 6V8" stroke="currentColor" stroke-width="2"/>
                    <path d="M13 13H17C17.55 13 18 12.55 18 12V8C18 7.45 17.55 7 17 7H13C12.45 7 12 7.45 12 8V12C12 12.55 12.45 13 13 13Z" stroke="currentColor" stroke-width="2"/>
                    <path d="M9 13V17C9 17.55 9.45 18 10 18H14C14.55 18 15 17.55 15 17V13" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </div>
              </div>
              
              <!-- 内容 -->
              <div class="card-content">
                <div class="card-number">
                  {{ formatNumber(summary.total_visits || 0) }}
                </div>
                <div class="card-label">总识别次数</div>
                <div class="card-badge">
                  <span>↗</span>
                  <span>+12.5%</span>
                </div>
              </div>
            </div>
            
            <!-- 覆盖省份卡片 -->
            <div class="stat-card" data-aos="fade-up" data-aos-delay="200">
              <div class="card-decoration"></div>
              
              <div class="card-icon-container">
                <div class="card-icon">
                  <svg class="icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2"/>
                    <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2"/>
                    <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </div>
              </div>
              
              <div class="card-content">
                <div class="card-number">
                  {{ summary.provinces_count || 0 }}
                </div>
                <div class="card-label">覆盖省份</div>
                <div class="card-badge">
                  <span>↗</span>
                  <span>+2</span>
                </div>
              </div>
            </div>
            
            <!-- 作物种类卡片 -->
            <div class="stat-card" data-aos="fade-up" data-aos-delay="300">
              <div class="card-decoration"></div>
              
              <div class="card-icon-container">
                <div class="card-icon">
                  <svg class="icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 20.5C12 20.5 20 16 20 10C20 6.13 16.87 3 13 3C11.84 3 10.5 3.5 9.5 4.5C8.5 3.5 7.16 3 6 3C3.13 3 0 6.13 0 10C0 16 8 20.5 8 20.5" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </div>
              </div>
              
              <div class="card-content">
                <div class="card-number">
                  {{ summary.crops_count || 0 }}
                </div>
                <div class="card-label">作物种类</div>
                <div class="card-badge">
                  <span>↗</span>
                  <span>+5</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 地图和热门排行 -->
          <div class="map-and-rankings">
            <!-- 中国地图 -->
            <div class="map-container" data-aos="fade-right">
              <div class="section-header">
                <h2 class="section-title">全国病害识别热力分布图</h2>
                <div class="title-decoration"></div>
              </div>
              <div ref="mapChartRef" class="chart-container map-chart"></div>
            </div>

            <!-- 热门排行榜 -->
            <div class="rankings-container">
              <!-- 热门省份 TOP5 -->
              <div class="ranking-card" data-aos="fade-left" data-aos-delay="100">
                <div class="ranking-header">
                  <h3 class="ranking-title">热门省份 TOP5</h3>
                  <div class="ranking-emoji">🏆</div>
                </div>
                
                <div class="ranking-list">
                  <div 
                    v-for="(item, index) in summary.top_provinces && summary.top_provinces.slice(0, 5) || []" 
                    :key="index" 
                    class="ranking-item"
                    :class="{ 'top-three': index < 3 }"
                  >
                    <!-- 排名 -->
                    <span 
                      class="ranking-number"
                      :class="{
                        'rank-gold': index === 0,
                        'rank-silver': index === 1,
                        'rank-bronze': index === 2,
                        'rank-normal': index > 2
                      }"
                    >
                      {{ index + 1 }}
                    </span>
                    
                    <!-- 省份名称 -->
                    <span class="ranking-name">{{ item.province }}</span>
                    
                    <!-- 数量 -->
                    <div class="ranking-count">
                      <span class="count-number">{{ item.count }}</span>
                      <span class="count-unit">次</span>
                    </div>
                    
                    <!-- 进度条 -->
                    <div class="progress-bar">
                      <div 
                        class="progress-fill"
                        :style="{ width: getProgressWidth(item.count, summary.top_provinces) }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 热门作物 TOP5 -->
              <div class="ranking-card" data-aos="fade-left" data-aos-delay="200">
                <div class="ranking-header">
                  <h3 class="ranking-title">热门作物 TOP5</h3>
                  <div class="ranking-emoji">🌱</div>
                </div>
                
                <div class="ranking-list">
                  <div 
                    v-for="(item, index) in summary.top_crops && summary.top_crops.slice(0, 5) || []" 
                    :key="index" 
                    class="ranking-item"
                    :class="{ 'top-three': index < 3 }"
                  >
                    <span 
                      class="ranking-number"
                      :class="{
                        'rank-gold': index === 0,
                        'rank-silver': index === 1,
                        'rank-bronze': index === 2,
                        'rank-normal': index > 2
                      }"
                    >
                      {{ index + 1 }}
                    </span>
                    
                    <span class="ranking-name">{{ item.crop_type }}</span>
                    
                    <div class="ranking-count">
                      <span class="count-number">{{ item.count }}</span>
                      <span class="count-unit">次</span>
                    </div>
                    
                    <div class="progress-bar">
                      <div 
                        class="progress-fill"
                        :style="{ width: getProgressWidth(item.count, summary.top_crops) }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 病害识别详情 -->
          <div class="disease-analysis" data-aos="fade-up">
            <div class="section-header">
              <h2 class="section-title centered">病害识别统计</h2>
              <div class="title-decoration"></div>
            </div>
            
            <div class="charts-grid">
              <!-- 病害识别分布图表 -->
              <div class="chart-card" data-aos="zoom-in" data-aos-delay="100">
                <div class="chart-header">
                  <h3 class="chart-title">病害识别分布</h3>
                  <div class="chart-emoji">📊</div>
                </div>
                <div ref="diseaseChartRef" class="chart-container"></div>
              </div>
              
              <!-- 作物-病害分布图表 -->
              <div class="chart-card" data-aos="zoom-in" data-aos-delay="200">
                <div class="chart-header">
                  <h3 class="chart-title">作物-病害分布</h3>
                  <div class="chart-emoji">🥧</div>
                </div>
                <div ref="cropDiseaseChartRef" class="chart-container"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import Sidebar from '@/components/sidebar/Sidebar.vue'
import { getStatsSummary, getSimpleVisitStats, getRecognitionStats } from '@/api/statistics.js'
import chinaJson from '@/assets/map/china.json'

export default {
  name: 'DiseaseAnalysisPage',
  components: {
    Sidebar
  },
  data() {
    return {
      isSidebarCollapsed: false,
      sidebarWidth: 318,
      loading: false,
      summary: {},
      visitStats: [],
      recognitionStats: {},
      mapChart: null,
      diseaseChart: null,
      cropDiseaseChart: null
    }
  },
  computed: {
    contentStyle() {
      if (this.isSidebarCollapsed) {
        return {
          marginLeft: '0px',
          width: '100%',
          transition: 'margin-left 0.3s, width 0.3s'
        }
      } else {
        return {
          marginLeft: `${this.sidebarWidth}px`,
          width: `calc(100% - ${this.sidebarWidth}px)`,
          transition: 'margin-left 0.3s, width 0.3s'
        }
      }
    }
  },
  methods: {
    handleSidebarToggle(collapsed) {
      this.isSidebarCollapsed = collapsed
      // 重新调整图表大小
      this.$nextTick(() => {
        if (this.mapChart) this.mapChart.resize()
        if (this.diseaseChart) this.diseaseChart.resize()
        if (this.cropDiseaseChart) this.cropDiseaseChart.resize()
      })
    },

    handleNewChat() {
      this.$router.push('/')
    },

    async loadData() {
      this.loading = true
      try {
        // 并行加载数据
        const [summaryData, visitStatsData, recognitionStatsData] = await Promise.all([
          getStatsSummary(),
          getSimpleVisitStats(),
          getRecognitionStats()
        ])
        
        console.log('=== API 数据加载调试 ===')
        console.log('1. 统计概览数据:', summaryData)
        console.log('2. 访问统计数据:', visitStatsData)
        console.log('3. 识别统计数据:', recognitionStatsData)
        
        Object.assign(this.summary, summaryData)
        this.visitStats = visitStatsData.stats || []
        Object.assign(this.recognitionStats, recognitionStatsData)
        
        console.log('4. 处理后的 visitStats:', this.visitStats)
        console.log('5. visitStats 长度:', this.visitStats.length)
        
      } catch (error) {
        console.error('加载数据失败:', error)
        // 使用模拟数据以便测试
        this.loadMockData()
      } finally {
        this.loading = false
      }
    },

    loadMockData() {
      console.log('=== 加载模拟数据 ===')
      
      // 模拟数据用于测试和演示
      Object.assign(this.summary, {
        total_visits: 1250,
        provinces_count: 23,
        crops_count: 15,
        top_provinces: [
          { province: '广东省', count: 186 },
          { province: '北京市', count: 145 },
          { province: '江苏省', count: 123 },
          { province: '浙江省', count: 98 },
          { province: '上海市', count: 87 }
        ],
        top_crops: [
          { crop_type: '番茄', count: 234 },
          { crop_type: '黄瓜', count: 189 },
          { crop_type: '辣椒', count: 156 },
          { crop_type: '玉米', count: 134 },
          { crop_type: '水稻', count: 123 }
        ]
      })
      
      this.visitStats = [
        { province: '北京市', crop_type: '番茄', count: 45 },
        { province: '北京市', crop_type: '黄瓜', count: 32 },
        { province: '广东省', crop_type: '番茄', count: 56 },
        { province: '广东省', crop_type: '辣椒', count: 43 },
        { province: '江苏省', crop_type: '水稻', count: 38 },
        { province: '浙江省', crop_type: '黄瓜', count: 35 },
        { province: '上海市', crop_type: '番茄', count: 29 },
        { province: '山东省', crop_type: '玉米', count: 41 },
        { province: '河南省', crop_type: '小麦', count: 33 },
        { province: '湖南省', crop_type: '水稻', count: 27 },
        { province: '四川省', crop_type: '辣椒', count: 31 },
        { province: '安徽省', crop_type: '玉米', count: 25 }
      ]
      
      console.log('模拟 visitStats:', this.visitStats)
      console.log('模拟 summary:', this.summary)
      
      Object.assign(this.recognitionStats, {
        disease_stats: [
          { disease_label: '灰霉病', count: 145, avg_confidence: 0.85 },
          { disease_label: '叶霉病', count: 132, avg_confidence: 0.78 },
          { disease_label: '白粉病', count: 98, avg_confidence: 0.82 },
          { disease_label: '晚疫病', count: 87, avg_confidence: 0.79 },
          { disease_label: '病毒病', count: 76, avg_confidence: 0.81 },
          { disease_label: '炭疽病', count: 65, avg_confidence: 0.83 },
          { disease_label: '枯萎病', count: 54, avg_confidence: 0.77 },
          { disease_label: '锈病', count: 43, avg_confidence: 0.80 },
          { disease_label: '霜霉病', count: 38, avg_confidence: 0.84 },
          { disease_label: '根腐病', count: 32, avg_confidence: 0.76 }
        ],
        crop_disease_stats: [
          { crop_type: '番茄', disease_label: '灰霉病', count: 89 },
          { crop_type: '番茄', disease_label: '叶霉病', count: 67 },
          { crop_type: '黄瓜', disease_label: '白粉病', count: 56 },
          { crop_type: '辣椒', disease_label: '病毒病', count: 45 },
          { crop_type: '番茄', disease_label: '晚疫病', count: 43 },
          { crop_type: '黄瓜', disease_label: '霜霉病', count: 38 },
          { crop_type: '玉米', disease_label: '锈病', count: 34 },
          { crop_type: '水稻', disease_label: '稻瘟病', count: 32 },
          { crop_type: '辣椒', disease_label: '炭疽病', count: 29 },
          { crop_type: '番茄', disease_label: '枯萎病', count: 27 },
          { crop_type: '黄瓜', disease_label: '枯萎病', count: 25 },
          { crop_type: '玉米', disease_label: '叶斑病', count: 23 },
          { crop_type: '水稻', disease_label: '纹枯病', count: 21 },
          { crop_type: '辣椒', disease_label: '疫病', count: 19 },
          { crop_type: '番茄', disease_label: '青枯病', count: 18 }
        ]
      })
      
      console.log('模拟数据加载完成')
    },

    initCharts() {
      // 注册中国地图
      echarts.registerMap('china', chinaJson)
      
      this.initMapChart()
      this.initDiseaseChart()
      this.initCropDiseaseChart()
    },

    processMapData() {
      console.log('=== 地图数据处理调试 ===')
      console.log('原始 visitStats:', this.visitStats)
      
      const provinceMap = {}
      
      this.visitStats.forEach((item, index) => {
        console.log(`处理第 ${index + 1} 项:`, item)
        const province = this.normalizeProvinceName(item.province)
        console.log(`${item.province} -> ${province}`)
        
        if (provinceMap[province]) {
          provinceMap[province] += item.count
        } else {
          provinceMap[province] = item.count
        }
      })
      
      console.log('省份聚合结果:', provinceMap)
      
      // 如果没有真实数据，使用模拟数据确保地图有显示效果
      if (Object.keys(provinceMap).length === 0) {
        console.log('没有真实数据，使用模拟数据')
        const mockProvinceData = {
          '北京': 186,
          '上海': 145,
          '广东': 234,
          '江苏': 189,
          '浙江': 156,
          '山东': 134,
          '河南': 123,
          '四川': 112,
          '湖北': 98,
          '湖南': 87,
          '安徽': 76,
          '河北': 65,
          '福建': 54,
          '江西': 43,
          '陕西': 38,
          '辽宁': 32,
          '山西': 28,
          '吉林': 25,
          '黑龙江': 22,
          '云南': 19
        }
        
        Object.keys(mockProvinceData).forEach(province => {
          provinceMap[province] = mockProvinceData[province]
        })
        
        console.log('使用模拟数据后的 provinceMap:', provinceMap)
      }
      
      const mapData = Object.keys(provinceMap).map(province => ({
        name: province,
        value: provinceMap[province]
      }))
      
      console.log('最终地图数据:', mapData)
      console.log('数据值范围:', Math.min(...mapData.map(d => d.value)), '-', Math.max(...mapData.map(d => d.value)))
      
      return mapData
    },

    normalizeProvinceName(name) {
      // 标准化省份名称，确保与地图数据匹配
      const provinceNames = {
        '北京市': '北京',
        '天津市': '天津', 
        '上海市': '上海',
        '重庆市': '重庆',
        '河北省': '河北',
        '山西省': '山西',
        '辽宁省': '辽宁',
        '吉林省': '吉林',
        '黑龙江省': '黑龙江',
        '江苏省': '江苏',
        '浙江省': '浙江',
        '安徽省': '安徽',
        '福建省': '福建',
        '江西省': '江西',
        '山东省': '山东',
        '河南省': '河南',
        '湖北省': '湖北',
        '湖南省': '湖南',
        '广东省': '广东',
        '海南省': '海南',
        '四川省': '四川',
        '贵州省': '贵州',
        '云南省': '云南',
        '陕西省': '陕西',
        '甘肃省': '甘肃',
        '青海省': '青海',
        '台湾省': '台湾',
        '内蒙古自治区': '内蒙古',
        '广西壮族自治区': '广西',
        '西藏自治区': '西藏',
        '宁夏回族自治区': '宁夏',
        '新疆维吾尔自治区': '新疆',
        '香港特别行政区': '香港',
        '澳门特别行政区': '澳门'
      }
      
      // 先检查是否是完整的省份名称
      if (provinceNames[name]) {
        return provinceNames[name]
      }
      
      // 如果已经是简化名称，直接返回
      const simplifiedNames = Object.values(provinceNames)
      if (simplifiedNames.includes(name)) {
        return name
      }
      
      // 尝试匹配部分名称
      for (const [fullName, shortName] of Object.entries(provinceNames)) {
        if (fullName.includes(name) || name.includes(shortName)) {
          return shortName
        }
      }
      
      return name
    },

    getColorByValue(value, maxValue) {
      // 根据数值计算对应的颜色
      const colors = [
        '#f1f8e9',   // 0-10%
        '#dcedc8',   // 10-20%
        '#c8e6c9',   // 20-30%
        '#a5d6a7',   // 30-40%
        '#81c784',   // 40-50%
        '#66bb6a',   // 50-60%
        '#4caf50',   // 60-70%
        '#43a047',   // 70-80%
        '#388e3c',   // 80-90%
        '#2e7d32',   // 90-95%
        '#1b5e20'    // 95-100%
      ]
      
      if (!value || maxValue === 0) return colors[0]
      
      const ratio = value / maxValue
      const index = Math.min(Math.floor(ratio * colors.length), colors.length - 1)
      return colors[index]
    },

    initMapChart() {
      if (!this.$refs.mapChartRef) return
      
      this.mapChart = echarts.init(this.$refs.mapChartRef)
      
      // 处理地图数据
      const mapData = this.processMapData()
      
      console.log('=== 地图初始化调试 ===')
      console.log('地图数据:', mapData)
      console.log('数据长度:', mapData.length)
      console.log('最大值:', Math.max(...mapData.map(item => item.value), 100))
      
      const option = {
        title: {
          text: '全国病害识别热力分布',
          left: 'center',
          top: 20,
          textStyle: {
            color: '#249745',
            fontSize: 20,
            fontWeight: 'bold'
          }
        },
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#249745',
          borderWidth: 1,
          textStyle: {
            color: '#333'
          },
          formatter: function(params) {
            console.log('Tooltip params:', params)
            if (params.componentType === 'series' && params.seriesType === 'heatmap') {
              return `<div style="padding: 8px;">
                <strong style="color: #249745;">热力点</strong><br/>
                识别密度：<span style="color: #249745; font-weight: bold;">${params.value[2] || 0}</span>
              </div>`
            } else {
              return `<div style="padding: 8px;">
                <strong style="color: #249745;">${params.name}</strong><br/>
                识别次数：<span style="color: #249745; font-weight: bold;">${params.value || 0}</span>次
              </div>`
            }
          }
        },
        geo: {
          map: 'china',
          roam: true,
          zoom: 1.2,
          center: [105, 36],
          itemStyle: {
            areaColor: '#fafafa',
            borderColor: 'rgba(0, 0, 0, 0.2)',
            borderWidth: 0.5
          },
          emphasis: {
            itemStyle: {
              areaColor: '#e8f5e8'
            }
          },
          regions: mapData.map(item => ({
            name: item.name,
            itemStyle: {
              areaColor: this.getColorByValue(item.value, Math.max(...mapData.map(d => d.value)))
            }
          }))
        },
        visualMap: [
          {
            // 地图区域的视觉映射
            min: 0,
            max: Math.max(...mapData.map(item => item.value), 100),
            left: 20,
            bottom: 80,
            text: ['高', '低'],
            calculable: true,
            inRange: {
              color: [
                '#f1f8e9',   // 最浅绿
                '#dcedc8',   // 浅绿
                '#c8e6c9',   // 较浅绿
                '#a5d6a7',   // 中浅绿
                '#81c784',   // 中绿
                '#66bb6a',   // 较深绿
                '#4caf50',   // 深绿
                '#43a047',   // 更深绿
                '#388e3c',   // 很深绿
                '#2e7d32',   // 最深绿
                '#1b5e20'    // 极深绿
              ]
            },
            textStyle: {
              color: '#333',
              fontSize: 12
            },
            itemWidth: 20,
            itemHeight: 120,
            seriesIndex: 0
          },
          {
            // 热力点的视觉映射
            min: 0,
            max: 200,
            left: 20,
            bottom: 20,
            text: ['热', '冷'],
            calculable: true,
            inRange: {
              color: [
                'rgba(76, 175, 80, 0.2)',   // 20% 透明度绿色
                'rgba(76, 175, 80, 0.4)',   // 40% 透明度绿色
                'rgba(76, 175, 80, 0.6)',   // 60% 透明度绿色
                'rgba(76, 175, 80, 0.8)',   // 80% 透明度绿色
                'rgba(76, 175, 80, 1.0)',   // 100% 不透明绿色
                'rgba(255, 152, 0, 0.9)'    // 橙色表示极高热度
              ]
            },
            textStyle: {
              color: '#333',
              fontSize: 12
            },
            itemWidth: 20,
            itemHeight: 60,
            seriesIndex: 1
          }
        ],
        series: [
          {
            // 地图区域数据
            name: '识别次数',
            type: 'map',
            map: 'china',
            geoIndex: 0,
            data: mapData,
            itemStyle: {
              borderColor: 'rgba(0, 0, 0, 0.2)',
              borderWidth: 0.5
            },
            emphasis: {
              itemStyle: {
                areaColor: '#43a047',
                borderWidth: 1,
                borderColor: '#2e7d32',
                shadowBlur: 15,
                shadowColor: 'rgba(67, 160, 71, 0.6)'
              },
              label: {
                show: true,
                color: '#fff',
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            label: {
              show: false
            }
          },
          {
            // 热力点数据
            name: '热力分布',
            type: 'heatmap',
            coordinateSystem: 'geo',
            data: this.generateHeatmapData(),
            pointSize: 20,
            blurSize: 30,
            itemStyle: {
              opacity: 0.6
            }
          },
          {
            // 散点图显示重点城市
            name: '重点区域',
            type: 'scatter',
            coordinateSystem: 'geo',
            data: this.generateScatterData(),
            symbolSize: function(val) {
              return Math.max(val[2] / 8, 12)
            },
            itemStyle: {
              color: {
                type: 'radial',
                x: 0.5,
                y: 0.5,
                r: 0.5,
                colorStops: [
                  { offset: 0, color: 'rgba(255, 255, 255, 0.9)' },
                  { offset: 0.6, color: 'rgba(76, 175, 80, 0.8)' },
                  { offset: 1, color: 'rgba(46, 125, 50, 1)' }
                ]
              },
              borderColor: 'rgba(255, 255, 255, 0.8)',
              borderWidth: 2,
              shadowBlur: 8,
              shadowColor: 'rgba(76, 175, 80, 0.4)'
            },
            emphasis: {
              itemStyle: {
                color: {
                  type: 'radial',
                  x: 0.5,
                  y: 0.5,
                  r: 0.5,
                  colorStops: [
                    { offset: 0, color: 'rgba(255, 255, 255, 1)' },
                    { offset: 0.6, color: 'rgba(255, 152, 0, 0.8)' },
                    { offset: 1, color: 'rgba(255, 87, 34, 1)' }
                  ]
                },
                borderColor: '#fff',
                borderWidth: 3,
                shadowBlur: 15,
                shadowColor: 'rgba(255, 152, 0, 0.6)'
              }
            }
          }
        ]
      }
      
      console.log('ECharts 配置:', option)
      
      this.mapChart.setOption(option)
      
      console.log('地图已初始化完成')
    },

    generateHeatmapData() {
      // 生成热力图数据点（模拟高密度识别区域）
      const heatmapData = []
      
      // 主要农业区域的经纬度和识别密度
      const majorAgriculturalAreas = [
        // 华北平原
        [116.4, 39.9, 180], // 北京
        [117.2, 39.1, 150], // 天津
        [114.5, 38.0, 120], // 石家庄
        [112.5, 37.9, 100], // 太原
        
        // 长江中下游平原
        [118.8, 32.0, 200], // 南京
        [120.2, 30.3, 190], // 杭州
        [121.5, 31.2, 160], // 上海
        [117.3, 31.9, 140], // 合肥
        [115.9, 28.7, 130], // 南昌
        
        // 华南地区
        [113.3, 23.1, 170], // 广州
        [114.1, 22.3, 140], // 深圳
        [110.3, 20.0, 110], // 海口
        
        // 四川盆地
        [104.1, 30.7, 160], // 成都
        [106.9, 29.6, 120], // 重庆
        
        // 关中平原
        [108.9, 34.3, 130], // 西安
        
        // 东北平原
        [125.3, 43.8, 140], // 长春
        [126.6, 45.8, 130], // 哈尔滨
        [123.4, 41.8, 120], // 沈阳
        
        // 河南平原
        [113.6, 34.8, 150], // 郑州
        [117.0, 36.7, 110], // 济南
        
        // 湖北江汉平原
        [114.3, 30.6, 140], // 武汉
        [112.9, 28.2, 120], // 长沙
      ]
      
      majorAgriculturalAreas.forEach(area => {
        // 为每个区域添加主要热力点
        heatmapData.push([area[0], area[1], area[2]])
        
        // 在周围添加一些辅助热力点
        for (let i = 0; i < 3; i++) {
          const offsetLng = (Math.random() - 0.5) * 2
          const offsetLat = (Math.random() - 0.5) * 2
          const intensity = area[2] * (0.3 + Math.random() * 0.4)
          heatmapData.push([
            area[0] + offsetLng,
            area[1] + offsetLat,
            Math.floor(intensity)
          ])
        }
      })
      
      return heatmapData
    },

    getProvinceCoordinates(provinceName) {
      // 省份坐标映射
      const provinceCoords = {
        '北京市': [116.4, 39.9],
        '天津市': [117.2, 39.1],
        '上海市': [121.5, 31.2],
        '重庆市': [106.9, 29.6],
        '河北省': [114.5, 38.0],
        '山西省': [112.5, 37.9],
        '辽宁省': [123.4, 41.8],
        '吉林省': [125.3, 43.8],
        '黑龙江省': [126.6, 45.8],
        '江苏省': [118.8, 32.0],
        '浙江省': [120.2, 30.3],
        '安徽省': [117.3, 31.9],
        '福建省': [119.3, 26.1],
        '江西省': [115.9, 28.7],
        '山东省': [117.0, 36.7],
        '河南省': [113.6, 34.8],
        '湖北省': [114.3, 30.6],
        '湖南省': [112.9, 28.2],
        '广东省': [113.3, 23.1],
        '海南省': [110.3, 20.0],
        '四川省': [104.1, 30.7],
        '贵州省': [106.7, 26.6],
        '云南省': [102.8, 25.0],
        '陕西省': [108.9, 34.3],
        '甘肃省': [103.8, 36.1],
        '青海省': [101.8, 36.6],
        '台湾省': [121.0, 23.5],
        '内蒙古自治区': [111.8, 40.8],
        '广西壮族自治区': [108.3, 22.8],
        '西藏自治区': [91.1, 29.6],
        '宁夏回族自治区': [106.3, 38.5],
        '新疆维吾尔自治区': [87.6, 43.8]
      }
      
      return provinceCoords[provinceName] || null
    },

    generateScatterData() {
      // 生成散点数据（重点监测区域）
      const scatterData = []
      
      // 基于实际访问统计数据生成散点
      this.visitStats.forEach(item => {
        const coordinates = this.getProvinceCoordinates(item.province)
        if (coordinates) {
          scatterData.push([
            coordinates[0], 
            coordinates[1], 
            item.count,
            item.province
          ])
        }
      })
      
      // 如果没有实际数据，使用与地图数据一致的模拟数据
      if (scatterData.length === 0) {
        const mapData = this.processMapData()
        mapData.forEach(item => {
          const coordinates = this.getProvinceCoordinates(item.name + '省') || 
                            this.getProvinceCoordinates(item.name + '市') || 
                            this.getProvinceCoordinates(item.name)
          if (coordinates) {
            scatterData.push([
              coordinates[0], 
              coordinates[1], 
              item.value,
              item.name
            ])
          }
        })
      }
      
      // 按数值排序，只取前10个重点区域
      return scatterData
        .sort((a, b) => b[2] - a[2])
        .slice(0, 10)
    },

    initDiseaseChart() {
      if (!this.$refs.diseaseChartRef) return
      
      this.diseaseChart = echarts.init(this.$refs.diseaseChartRef)
      
      const diseaseData = this.recognitionStats.disease_stats?.slice(0, 10) || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#249745',
          borderWidth: 1,
          textStyle: {
            color: '#333'
          },
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(36, 151, 69, 0.1)'
            }
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: diseaseData.map(item => item.disease_label),
          axisLabel: {
            rotate: 45,
            interval: 0,
            color: '#666',
            fontSize: 12
          },
          axisLine: {
            lineStyle: {
              color: '#e0e0e0'
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '识别次数',
          nameTextStyle: {
            color: '#666',
            fontSize: 14
          },
          axisLabel: {
            color: '#666'
          },
          axisLine: {
            lineStyle: {
              color: '#e0e0e0'
            }
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0'
            }
          }
        },
        series: [
          {
            data: diseaseData.map((item, index) => ({
              value: item.count,
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: index < 3 ? '#66bb6a' : '#249745' },
                    { offset: 1, color: index < 3 ? '#2e7d32' : '#1b5e20' }
                  ]
                }
              }
            })),
            type: 'bar',
            barWidth: '60%',
            emphasis: {
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: '#81c784' },
                    { offset: 1, color: '#388e3c' }
                  ]
                }
              }
            }
          }
        ]
      }
      
      this.diseaseChart.setOption(option)
    },

    initCropDiseaseChart() {
      if (!this.$refs.cropDiseaseChartRef) return
      
      this.cropDiseaseChart = echarts.init(this.$refs.cropDiseaseChartRef)
      
      const cropDiseaseData = this.recognitionStats.crop_disease_stats?.slice(0, 12) || []
      
      // 生成绿色系渐变色彩
      const colors = [
        '#66bb6a', '#4caf50', '#43a047', '#388e3c', 
        '#2e7d32', '#1b5e20', '#81c784', '#a5d6a7',
        '#c8e6c9', '#e8f5e8', '#16a085', '#27ae60'
      ]
      
      const option = {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#249745',
          borderWidth: 1,
          textStyle: {
            color: '#333'
          },
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 10,
          top: 20,
          bottom: 20,
          textStyle: {
            color: '#666',
            fontSize: 12
          }
        },
        series: [
          {
            name: '作物-病害分布',
            type: 'pie',
            radius: ['45%', '75%'],
            center: ['40%', '50%'],
            data: cropDiseaseData.map((item, index) => ({
              name: `${item.crop_type}-${item.disease_label}`,
              value: item.count,
              itemStyle: {
                color: colors[index % colors.length],
                borderWidth: 2,
                borderColor: '#fff'
              }
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 15,
                shadowOffsetX: 0,
                shadowColor: 'rgba(36, 151, 69, 0.5)',
                borderWidth: 3,
                borderColor: '#249745'
              }
            },
            labelLine: {
              show: false
            },
            label: {
              show: false
            }
          }
        ]
      }
      
      this.cropDiseaseChart.setOption(option)
    },

    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    },

    getProgressWidth(count, list) {
      if (!list || list.length === 0) return '0%'
      const maxCount = Math.max(...list.map(item => item.count))
      return `${(count / maxCount) * 100}%`
    }
  },

  // 生命周期钩子
  async mounted() {    
    await this.loadData()
    
    // 添加调试信息
    console.log('=== 组件挂载完成调试 ===')
    console.log('visitStats:', this.visitStats)
    console.log('summary:', this.summary)
    console.log('recognitionStats:', this.recognitionStats)
    
    this.$nextTick(() => {
      this.initCharts()
    })
  },

  beforeDestroy() {
    if (this.mapChart) {
      this.mapChart.dispose()
    }
    if (this.diseaseChart) {
      this.diseaseChart.dispose()
    }
    if (this.cropDiseaseChart) {
      this.cropDiseaseChart.dispose()
    }
  }
}
</script>

<style scoped>
/* 页面容器 */
.page-container {
  display: flex;
  height: 100vh;
  background-color: #f9fafb;
}

/* 侧边栏 */
.sidebar-container {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 50;
  transition: transform 0.3s ease-in-out;
}

.sidebar-collapsed {
  transform: translateX(-100%);
}

/* 主要内容 */
.main-content {
  position: relative;
  height: 100vh;
  overflow-y: auto;
  background: linear-gradient(135deg, #f9fafb 0%, #f0fdf4 50%, #f9fafb 100%);
  transition: all 0.3s ease-in-out;
}

.content-wrapper {
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px;
}

@media (min-width: 1024px) {
  .content-wrapper {
    padding: 40px;
  }
}

/* 页面标题 */
.page-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: bold;
  background: linear-gradient(to right, #059669, #047857, #0d9488);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  margin-bottom: 48px;
  position: relative;
}

@media (min-width: 1024px) {
  .page-title {
    font-size: 3rem;
  }
}

.title-underline {
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 96px;
  height: 4px;
  background: linear-gradient(to right, #059669, #0d9488);
  border-radius: 9999px;
}

/* 加载状态 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 384px;
  color: #6b7280;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #059669;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 1.125rem;
  margin: 0;
}

/* 主要部分 */
.main-sections {
  display: flex;
  flex-direction: column;
  gap: 48px;
}

/* 统计网格 */
.stats-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1280px) {
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
}

/* 统计卡片 */
.stat-card {
  position: relative;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  transform: translateY(0);
}

.stat-card:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transform: translateY(-8px);
}

.card-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, #059669, #0d9488, #10b981);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.card-icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.card-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #059669, #0d9488);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.icon-svg {
  width: 32px;
  height: 32px;
  color: white;
}

.card-content {
  text-align: center;
}

.card-number {
  font-size: 2.5rem;
  font-weight: bold;
  background: linear-gradient(to right, #059669, #047857);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  margin-bottom: 8px;
}

@media (min-width: 1024px) {
  .card-number {
    font-size: 3rem;
  }
}

.card-label {
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 16px;
}

.card-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #ccfbf1;
  color: #0f766e;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 9999px;
}

/* 地图和排行榜 */
.map-and-rankings {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

@media (min-width: 1280px) {
  .map-and-rankings {
    flex-direction: row;
  }
}

.map-container {
  flex: 2;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.map-container:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  transform: translateY(-4px);
}

.rankings-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #dcfce7;
}

.section-title {
  font-size: 1.5rem;
  font-weight: bold;
  background: linear-gradient(to right, #059669, #047857);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.section-title.centered {
  text-align: center;
  flex: 1;
  font-size: 1.875rem;
}

.title-decoration {
  width: 48px;
  height: 4px;
  background: linear-gradient(to right, #059669, #0d9488);
  border-radius: 9999px;
}

.chart-container {
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.map-chart {
  height: 384px;
}

@media (min-width: 1024px) {
  .map-chart {
    height: 500px;
  }
}

/* 排行榜卡片 */
.ranking-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.ranking-card:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  transform: translateY(-4px);
}

.ranking-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.ranking-title {
  font-size: 1.25rem;
  font-weight: bold;
  background: linear-gradient(to right, #059669, #047857);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.ranking-emoji {
  font-size: 1.5rem;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    transform: translate3d(0, -30px, 0);
  }
  70% {
    transform: translate3d(0, -15px, 0);
  }
  90% {
    transform: translate3d(0, -4px, 0);
  }
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ranking-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  border: 1px solid #dcfce7;
  transition: all 0.3s ease;
}

.ranking-item:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateX(8px);
}

.ranking-item.top-three {
  background: linear-gradient(to right, #f0fdf4, #ecfdf5);
  border-color: #bbf7d0;
}

.ranking-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: white;
  font-size: 0.875rem;
  font-weight: bold;
  margin-right: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.rank-gold {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

.rank-silver {
  background: linear-gradient(135deg, #d1d5db, #9ca3af);
}

.rank-bronze {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.rank-normal {
  background: linear-gradient(135deg, #059669, #0d9488);
}

.ranking-name {
  flex: 1;
  font-weight: 600;
  color: #374151;
}

.ranking-count {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-right: 16px;
}

.count-number {
  font-size: 1.125rem;
  font-weight: bold;
  color: #059669;
}

.count-unit {
  font-size: 0.75rem;
  color: #6b7280;
}

.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background-color: #dcfce7;
  border-radius: 0 0 16px 16px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(to right, #059669, #0d9488);
  transition: all 1s ease-out;
  border-radius: 0 4px 4px 0;
}

/* 病害分析 */
.disease-analysis {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.disease-analysis:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  transform: translateY(-4px);
}

.charts-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
}

@media (min-width: 1024px) {
  .charts-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.chart-card {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #dcfce7;
  transition: all 0.3s ease;
}

.chart-card:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-4px);
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.chart-title {
  font-size: 1.25rem;
  font-weight: 600;
  background: linear-gradient(to right, #059669, #047857);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.chart-emoji {
  font-size: 1.5rem;
  animation: pulse 2s infinite;
}

.chart-card .chart-container {
  height: 384px;
  border-radius: 12px;
  overflow: hidden;
}
</style>
